import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { useAssets } from '../../context/AssetContext';
import { useToast, ToastContainer } from '../common/Toast';
import { assetService } from '../../services/assetService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import AssetForm from './AssetForm';

const AssetEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateAsset, loading, error, clearError } = useAssets();
  const { toasts, removeToast, success, error: showError } = useToast();

  const [asset, setAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load asset data
  useEffect(() => {
    const loadAsset = async () => {
      try {
        const response = await assetService.getAsset(id);
        // Handle different response structures
        let assetData = null;
        if (response?.data?.asset) {
          // Backend returns { success: true, data: { asset } }
          assetData = response.data.asset;
        } else if (response?.data) {
          // Direct data object
          assetData = response.data;
        } else if (response?.asset) {
          // Nested asset property
          assetData = response.asset;
        } else {
          // Fallback to response itself
          assetData = response;
        }

        console.log('Loaded asset data:', assetData);
        setAsset(assetData);
      } catch (error) {
        console.error('Error loading asset:', error);
        showError('Failed to load asset data');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadAsset();
    }
  }, [id]); // Removed showError to prevent infinite rendering loop

  const handleSave = async (assetData) => {
    setIsSaving(true);
    try {
      const updatedAsset = await updateAsset(id, assetData);
      success('Asset updated successfully!');
      navigate('/assets/management');
      return updatedAsset;
    } catch (error) {
      console.error('Error updating asset:', error);
      showError('Failed to update asset');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/assets/management');
  };

  if (loading || isLoading) {
    return (
      <>
        <PageBreadcrumb
          items={[
            { label: 'Dashboard', path: '/overview' },
            { label: 'Assets', path: '/assets' },
            { label: 'Edit', path: null, isLast: true }
          ]}
        />
        <PageHeader
          title="Edit Asset"
          description="Loading asset information..."
        />
        <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!asset && !isLoading) {
    return (
      <>
        <PageBreadcrumb
          items={[
            { label: 'Dashboard', path: '/overview' },
            { label: 'Assets', path: '/assets' },
            { label: 'Edit', path: null, isLast: true }
          ]}
        />
        <PageHeader
          title="Asset Not Found"
          description="The requested asset could not be found"
        />
        <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="project-form-card" style={{ width: '100%' }}>
            <div className="card-body text-center py-5">
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <h3>Asset Not Found</h3>
              <p className="text-muted">
                The asset with ID "{id}" could not be found. It may have been deleted or the ID may be incorrect.
              </p>
              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  onClick={() => navigate('/assets')}
                  className="btn btn-primary"
                >
                  Back to Asset List
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-outline-secondary"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb
        items={[
          { label: 'Dashboard', path: '/overview' },
          { label: 'Assets', path: '/assets' },
          { label: 'Edit', path: null, isLast: true }
        ]}
      />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Edit Asset"
        description="Update asset information and details"
        actions={[
          {
            label: 'Asset Edit Help',
            icon: <HelpCircle size={18} />,
            onClick: () => {
              window.open('/help/assets/asset-management.html', '_blank');
            },
            variant: 'outline',
            tooltip: 'Asset Edit Help'
          }
        ]}
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: '24px' }}>
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearError}
              style={{ float: 'right' }}
            ></button>
          </div>
        )}

        {/* Form */}
        <div className="project-form-card" style={{ width: '100%' }}>
          {asset && (
            <AssetForm
              key={asset._id || id}
              asset={asset}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}
        </div>

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default AssetEdit;